import { FC } from "react";
import Image from "next/image";

import styles from "./BottomActions.module.css";

import { Receipt, ExtendedViewItem } from "@/types/receipt";
import { translate } from "@/utils/formatters";
import { getButtonText } from "@/utils/extendedViewHelpers";
import { Button } from "@/components/common";
import {
  Accounting,
  SecondHand,
  Insurance,
  ChevronDown,
} from "@/components/icons";
import { IconProps } from "@/components/icons/IconProps";
import { useExtendedWidgetVisibility } from "@/hooks/useExtendedWidgetVisibility";
import { useActiveAccountingWidget } from "@/hooks/useActiveAccountingWidget";
import {
  ACCOUNTING_WIDGET_TYPE,
  GLOBAL_WIDGET_TYPES,
  GlobalWidgetType,
  PopupArticle,
  QUICK_SEND_ACCOUNTING_POPUP_TYPE,
} from "@/constants/popup";

const GLOBAL_WIDGET_BUTTON_CONFIG: Record<
  GlobalWidgetType,
  { icon: FC<IconProps>; actionType: string }
> = {
  zeipt_accounting: { icon: Accounting, actionType: "accounting" },
  zeipt_second_hand: { icon: SecondHand, actionType: "secondhand" },
  zeipt_insurance_claims: { icon: Insurance, actionType: "insurance" },
};

interface BottomActionsProps {
  receipt: Receipt;
  lang: string;
  onAction: (
    type: string,
    index?: number,
    article?: PopupArticle
  ) => void;
  extendedViewItems?: ExtendedViewItem[];
  extendedViewImages?: { [key: string]: string };
}

export const BottomActions = ({
  receipt,
  lang,
  onAction,
  extendedViewItems,
  extendedViewImages,
}: BottomActionsProps) => {
  const { bottomWidgets, categorizedGlobalWidgets, serviceWidgets } =
    useExtendedWidgetVisibility({
      extended_view_items: extendedViewItems,
      extended_view_images: extendedViewImages,
      currentView: "bottom_view",
    });

  const { activeWidgetId } = useActiveAccountingWidget();

  const hasGlobalWidgets = Object.values(categorizedGlobalWidgets).some(
    (arr) => arr.length > 0
  );

  const activeAccountingItem = activeWidgetId
    ? categorizedGlobalWidgets[ACCOUNTING_WIDGET_TYPE].find(
        (i) => i.receipt_view_service_settings_id === activeWidgetId
      )
    : undefined;

  const hasServiceButtons =
    receipt.extended_receipt_logic?.discounts ||
    receipt.extra_receipt_view?.barcode ||
    serviceWidgets.length > 0;

  if (!hasServiceButtons && !bottomWidgets.length && !hasGlobalWidgets) {
    return null;
  }

  return (
    <div className="sticky-bottom">
      {/* Service Section */}
      {hasServiceButtons && (
        <div className={styles.serviceSection}>
          <h5 className={styles.sectionTitle}>
            {translate(lang, "Butikkstilbud", "Services")}
          </h5>
          <div className={styles.serviceButtons}>
            {receipt.extra_receipt_view?.barcode && (
              <Button
                type="button"
                className={styles.button}
                onClick={() => onAction("return")}
                text={translate(lang, "Retur & Skann kvittering", "Return")}
              />
            )}

            {receipt.extended_receipt_logic?.discounts && (
              <Button
                type="button"
                className={styles.button}
                onClick={() => onAction("discounts")}
                text={translate(lang, "Vis kuponger", "Show coupons")}
              />
            )}

            {serviceWidgets.length > 0 && (
              <Button
                type="button"
                className={styles.button}
                onClick={() => onAction("extended_view")}
                text={translate(
                  lang,
                  "Kvitteringsservices",
                  "Receipt Services"
                )}
              />
            )}
          </div>
        </div>
      )}

      {/* Widgets Section */}
      {bottomWidgets.length > 0 && (
        <div className={styles.widgetsSection}>
          <h5 className={styles.sectionTitle}>
            {translate(lang, "Partner Tjenester", "Widgets")}
          </h5>
          <div className={styles.widgetsContainer}>
            {bottomWidgets.map((item) => (
              <Button
                key={`widget-${item.extended_view_position}-${item.originalIndex}`}
                type="button"
                className={styles.widgetButton}
                onClick={() =>
                  onAction(
                    `widget_${item.extended_view_position}`,
                    item.originalIndex
                  )
                }
                text={getButtonText(item.extended_view_position, lang, {
                  receipt_button_text: item.receipt_button_text,
                })}
                icon={
                  item.imageSrc ? (
                    <Image
                      src={item.imageSrc}
                      alt={item.title}
                      width={20}
                      height={20}
                      className={styles.widgetIcon}
                    />
                  ) : undefined
                }
              />
            ))}
          </div>
        </div>
      )}

      {/* Global Widgets Section */}
      {hasGlobalWidgets && (
        <div className={styles.globalWidgetsContainer}>
          {GLOBAL_WIDGET_TYPES.map((widgetType) => {
            if (categorizedGlobalWidgets[widgetType].length === 0) return null;
            const { icon: Icon, actionType } =
              GLOBAL_WIDGET_BUTTON_CONFIG[widgetType];

            if (
              widgetType === ACCOUNTING_WIDGET_TYPE &&
              activeAccountingItem
            ) {
              const providerLabel = translate(
                lang,
                `Send til ${activeAccountingItem.title}`,
                `Send to ${activeAccountingItem.title}`
              );

              return (
                <div key={widgetType} className={styles.splitButtonContainer}>
                  <Button
                    type="button"
                    className={`${styles.globalWidgetButton} ${styles.splitButtonMain}`}
                    onClick={() => onAction(QUICK_SEND_ACCOUNTING_POPUP_TYPE)}
                    text={providerLabel}
                    icon={<Icon />}
                    data-action-button="true"
                    data-action-type={actionType}
                  />
                  <button
                    type="button"
                    className={styles.splitButtonChevron}
                    onClick={() => onAction(widgetType)}
                    aria-label={translate(
                      lang,
                      "Bytt regnskapssystem",
                      "Change accounting system"
                    )}
                  >
                    <span className={styles.splitButtonChevronLabel}>
                      {translate(lang, "Bytt", "Change")}
                    </span>
                    <span className={styles.splitButtonChevronIcon}>
                      <ChevronDown size={14} />
                    </span>
                  </button>
                </div>
              );
            }

            return (
              <Button
                key={widgetType}
                type="button"
                className={styles.globalWidgetButton}
                onClick={() => onAction(widgetType)}
                text={getButtonText(widgetType, lang)}
                icon={<Icon />}
                data-action-button="true"
                data-action-type={actionType}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
